/**
 * Keeps the site locale and the URL in step on public and docs pages.
 *
 * - /ja/...          the prefix is the locale.
 * - unprefixed page  English, unless the visitor prefers another language
 *                    (a stored choice, else the browser language): then they
 *                    are sent to that language's URL. Crawlers arrive with no
 *                    stored choice and an English browser, so they stay put.
 * - ?lang=xx         the old way to ask for a language; it now redirects to
 *                    the prefixed URL and drops the parameter.
 *
 * App pages (sign-in, dashboard, admin) are left alone and keep using the
 * stored locale.
 */

import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPreferredLocale, useI18n } from './i18n';
import { isLocale, isLocalizedPage, localizePath, splitLocalePath, DEFAULT_LOCALE, type Locale } from './locale-path';
import { headLinks } from './prerender-page';

export function LocaleRouteSync() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale } = useI18n();

  useEffect(() => {
    const { locale: prefix, path } = splitLocalePath(pathname);
    if (!isLocalizedPage(path)) return;

    const params = new URLSearchParams(search);
    const asked = params.get('lang');
    params.delete('lang');
    const rest = (params.toString() ? `?${params}` : '') + hash;

    // An explicit ?lang= is a choice: remember it, or the unprefixed English
    // page it leads to would send a visitor who stored another language back.
    if (isLocale(asked)) setLocale(asked);
    const target = isLocale(asked) ? asked : prefix ?? getPreferredLocale();
    const url = localizePath(path, target) + rest;
    if (url !== pathname + search + hash) {
      navigate(url, { replace: true });
      return;
    }
    if (locale !== (prefix ?? DEFAULT_LOCALE)) setLocale(prefix ?? DEFAULT_LOCALE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  // The prerender wrote canonical and hreflang for the page first loaded;
  // keep them describing the page on screen after client-side navigation.
  useEffect(() => {
    document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    for (const l of headLinks(pathname)) {
      const el = document.createElement('link');
      el.rel = l.rel;
      if (l.hreflang) el.hreflang = l.hreflang;
      el.href = l.href;
      document.head.appendChild(el);
    }
  }, [pathname]);

  return null;
}

/**
 * Switches the site language. On a public or docs page it also moves to that
 * page's URL in the new language; elsewhere only the stored choice changes.
 */
export function useSwitchLocale(): (next: Locale) => void {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { setLocale } = useI18n();
  return useCallback(
    (next: Locale) => {
      setLocale(next);
      const { path } = splitLocalePath(pathname);
      if (isLocalizedPage(path)) navigate(localizePath(path, next) + search + hash);
    },
    [pathname, search, hash, navigate, setLocale],
  );
}
